import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchPreviewContext, fetchPreviewGallery } from '../api/ventura/preview';
import GalleryOptionsPanel from '../components/PreviewStudio/GalleryOptionsPanel';
import GalleryPresentationCard from '../components/PreviewStudio/GalleryPresentationCard';
import GalleryPreviewCard from '../components/PreviewStudio/GalleryPreviewCard';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';
import {
  ChromeTheme,
  DeviceType,
  GalleryPreview,
  GalleryResponse,
  GalleryViewMode,
  Orientation,
  SiteContextType,
} from '../types/previewStudio';
import { captureCard } from '../util/galleryCapture';
import {
  downloadMultiplePNG,
  downloadPPTX,
  downloadSinglePNG,
} from '../util/galleryExport';
import { extractVideoInfo } from '../util/galleryVideoUtils';
import posthog from '../util/posthog';
import { getSizeProfile } from '../util/sizeProfiles';

// ---- Sort helpers ----

function sortPreviews(previews: GalleryPreview[], sortBy: 'name' | 'size' | 'type'): GalleryPreview[] {
  const sorted = [...previews];
  switch (sortBy) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'size':
      sorted.sort((a, b) => ((a.width || 0) * (a.height || 0)) - ((b.width || 0) * (b.height || 0)));
      break;
    case 'type': {
      const getType = (p: GalleryPreview) => {
        if (p.is_video) return 'video';
        if (p.is_mraid) return 'mraid';
        return 'banner';
      };
      sorted.sort((a, b) => getType(a).localeCompare(getType(b)));
    }
      break;
    default:
      break;
  }
  return sorted;
}

// ---- Main Gallery Page ----

export default function PreviewGalleryPage() {
  const { publicRoute } = useParams<{ publicRoute: string }>();
  const { apiHost: configApiHost } = useConfig();
  const auth = useAuth();
  const isAuthenticated = !!auth?.user;

  // For public gallery links, resolve the correct API host based on the deployed hostname
  // when localStorage has no apiHost set (e.g., incognito/client access defaults to localhost:3000)
  const resolveApiHost = (): string => {
    if (configApiHost !== 'http://localhost:3000') return configApiHost;
    const {hostname} = window.location;
    if (hostname.includes('-qa.') || hostname.includes('staging.')) return 'https://ventura-qa.thinknearhub.com';
    if (hostname === 'localhost') return 'https://dashboard.thinknear.com';
    return 'https://dashboard.thinknear.com';
  };
  const apiHost = resolveApiHost();
  const [gallery, setGallery] = useState<GalleryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<GalleryViewMode>('standard');
  const [optionsPanelOpen, setOptionsPanelOpen] = useState(false);

  // Presentation options — per-creative device types with smart defaults
  const [deviceTypeOverrides, setDeviceTypeOverrides] = useState<Map<number, DeviceType>>(new Map());
  const [siteContext, setSiteContext] = useState<SiteContextType>('blank');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [chromeTheme, setChromeTheme] = useState<ChromeTheme>('light');
  const [showSizeLabel, setShowSizeLabel] = useState(false);
  const [showNameLabel, setShowNameLabel] = useState(false);
  const [zoomScale, setZoomScale] = useState(0.5);
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'type'>('size');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  // Get effective device type for a creative: override > size profile default
  const getEffectiveDeviceType = useCallback((preview: GalleryPreview): DeviceType => {
    const override = deviceTypeOverrides.get(preview.creative_id);
    if (override) return override;
    if (preview.is_video && (!preview.width || !preview.height)) {
      const vi = extractVideoInfo(preview);
      if (vi) {
        return getSizeProfile(`${vi.width}x${vi.height}`).bestDevice;
      }
    }
    const size = `${preview.width || 300}x${preview.height || 250}`;
    return getSizeProfile(size).bestDevice;
  }, [deviceTypeOverrides]);

  const handleCreativeDeviceTypeChange = useCallback((creativeId: number, dt: DeviceType) => {
    setDeviceTypeOverrides((prev) => {
      const next = new Map(prev);
      next.set(creativeId, dt);
      return next;
    });
  }, []);

  const handleGlobalDeviceTypeChange = useCallback((dt: DeviceType) => {
    if (!gallery) return;
    setDeviceTypeOverrides((prev) => {
      const next = new Map(prev);
      gallery.previews.forEach((p) => next.set(p.creative_id, dt));
      return next;
    });
  }, [gallery]);

  // Refs for capture
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    async function loadGallery() {
      if (!publicRoute) {
        setError('No gallery ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchPreviewGallery(apiHost, publicRoute);

        // Enrich previews missing adm — for hosted images, fetch the creative's
        // image URL from the context API and build an <img> tag as adm
        const previewsMissingAdm = data.previews.filter((p) => !p.adm && p.status === 'ready');
        if (previewsMissingAdm.length > 0) {
          try {
            const ctx = await fetchPreviewContext(apiHost, { campaign_id: data.campaign_id });
            const targetedCreatives = ctx.context?.targeted_creatives || [];

            previewsMissingAdm.forEach((p) => {
              const tc = targetedCreatives.find(
                (t: any) => (t.creative?.id ?? t.creative_id) === p.creative_id
              );
              const link = tc?.creative?.link;
              if (link) {
                const pw = p.width || 300;
                const ph = p.height || 250;
                const imgHtml = `<img src="${link}" width="${pw}" height="${ph}" style="display:block;max-width:100%;height:auto;" alt="${p.name}" />`;
                // eslint-disable-next-line no-param-reassign
                p.adm = btoa(imgHtml);
              }
            });
          } catch {
            // Not authenticated or context fetch failed — hosted images will show as iframe fallback
          }
        }

        // Load pre-captured screenshots from localStorage (saved during gallery creation)
        if (publicRoute) {
          try {
            const savedScreenshots = localStorage.getItem(`gallery_screenshots_${publicRoute}`);
            if (savedScreenshots) {
              const screenshots = JSON.parse(savedScreenshots) as Record<string, string>;
              let applied = 0;
              data.previews.forEach((preview) => {
                const url = screenshots[preview.creative_id];
                if (url && !preview.screenshot_url) {
                  // eslint-disable-next-line no-param-reassign
                  preview.screenshot_url = url;
                  applied += 1;
                }
              });
              // eslint-disable-next-line no-console
              if (applied > 0) console.log(`[Gallery] Loaded ${applied} screenshots from localStorage`);
            }
          } catch {
            // localStorage unavailable
          }
        }

        // For previews that have adm but no screenshot, capture via Lambda now
        const previewsNeedingScreenshots = data.previews.filter(
          (p) => p.adm && p.status === 'ready' && !p.screenshot_url
        );
        if (previewsNeedingScreenshots.length > 0) {
          try {
            const { captureCreativeScreenshot } = await import('../util/galleryExport');
            // eslint-disable-next-line no-console
            console.log(`[Gallery] Capturing ${previewsNeedingScreenshots.length} missing screenshots...`);
            const screenshots: Record<number, string> = {};

            await Promise.allSettled(
              previewsNeedingScreenshots.map(async (p) => {
                try {
                  const htmlTag = atob(p.adm!);
                  const size = `${p.width || 300}x${p.height || 250}`;
                  const url = await captureCreativeScreenshot(htmlTag, size);
                  if (url) {
                    // eslint-disable-next-line no-param-reassign
                    p.screenshot_url = url;
                    screenshots[p.creative_id] = url;
                    // eslint-disable-next-line no-console
                    console.log(`[Gallery] ✓ Screenshot for creative ${p.creative_id}`);
                  }
                } catch {
                  // Capture failed
                }
              })
            );

            // Save to localStorage for future visits
            if (publicRoute && Object.keys(screenshots).length > 0) {
              try {
                const existing = JSON.parse(localStorage.getItem(`gallery_screenshots_${publicRoute}`) || '{}');
                localStorage.setItem(
                  `gallery_screenshots_${publicRoute}`,
                  JSON.stringify({ ...existing, ...screenshots })
                );
              } catch { /* localStorage full */ }
            }
          } catch {
            // Lambda not available — presentation will use iframe fallback
          }
        }

        setGallery(data);

        // Initialize from gallery config defaults
        if (data.gallery_config?.default_view_mode) {
          setViewMode(data.gallery_config.default_view_mode);
          if (data.gallery_config.default_view_mode === 'presentation') {
            setOptionsPanelOpen(true);
          }
        }
        if (data.gallery_config?.default_site_context) {
          setSiteContext(data.gallery_config.default_site_context);
        }
        // gallery_config.device_type is a legacy global setting — ignore it
        // in favor of per-size smart defaults from getSizeProfile().bestDevice
      } catch (err) {
        posthog.captureException(err instanceof Error ? err : new Error('gallery_load_failed'));
        setError(err instanceof Error ? err.message : 'Failed to load gallery');
      } finally {
        setLoading(false);
      }
    }

    loadGallery();
  }, [publicRoute, apiHost]);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (gallery) {
      setSelectedIds(new Set(gallery.previews.map((p) => p.creative_id)));
    }
  }, [gallery]);

  const handleSelectNone = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleDownloadPNG = useCallback(async () => {
    if (!gallery || selectedIds.size === 0) return;
    setIsExporting(true);
    try {
      const selected = gallery.previews.filter((p) => selectedIds.has(p.creative_id));

      if (viewMode === 'presentation') {
        const html2canvas = (await import('html2canvas')).default;
        const saveAs = (await import('file-saver')).saveAs || (await import('file-saver') as any).default?.saveAs || (await import('file-saver') as any).default;

        if (selected.length === 1) {
          const el = cardRefs.current.get(selected[0].creative_id);
          if (el) {
            const canvas = await captureCard(el, html2canvas);
            canvas.toBlob((blob: Blob | null) => { if (blob) saveAs(blob, `${selected[0].name}.png`); });
          }
        } else {
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          // Sequential capture to avoid memory spikes with large galleries
          // eslint-disable-next-line no-restricted-syntax
          for (const p of selected) {
            const el = cardRefs.current.get(p.creative_id);
            if (el) {
              // eslint-disable-next-line no-await-in-loop
              const canvas = await captureCard(el, html2canvas);
              const dataUrl = canvas.toDataURL('image/png');
              zip.file(`${p.name}.png`, dataUrl.split(',')[1], { base64: true });
            }
          }
          const blob = await zip.generateAsync({ type: 'blob' });
          saveAs(blob, 'gallery-previews.zip');
        }
      } else {
        const exportCreatives = selected.map((p) => ({
          name: p.name,
          size: `${p.width || 300}x${p.height || 250}`,
          adm: p.adm,
          screenshot_url: p.screenshot_url,
          deviceType: getEffectiveDeviceType(p),
          siteContext,
        }));
        if (exportCreatives.length === 1) {
          await downloadSinglePNG(exportCreatives[0]);
        } else {
          await downloadMultiplePNG(exportCreatives);
        }
      }
    } finally {
      setIsExporting(false);
    }
  }, [gallery, selectedIds, getEffectiveDeviceType, siteContext, viewMode]);

  const handleDownloadPPTX = useCallback(async () => {
    if (!gallery || selectedIds.size === 0) return;
    setIsExporting(true);
    try {
      const selected = gallery.previews.filter((p) => selectedIds.has(p.creative_id));

      if (viewMode === 'presentation') {
        const html2canvas = (await import('html2canvas')).default;

        // Load pptxgenjs
        const pptxMod = await new Promise<any>((resolve) => {
          if ((window as any).PptxGenJS) { resolve((window as any).PptxGenJS); return; }
          const script = document.createElement('script');
          script.src = '/pptxgen.bundle.js';
          script.onload = () => resolve((window as any).PptxGenJS);
          document.head.appendChild(script);
        });

        // eslint-disable-next-line new-cap
        const pptx = new pptxMod();
        pptx.layout = 'LAYOUT_WIDE';

        const captures: Array<{ name: string; size: string; dataUrl: string; aspectRatio: number }> = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const p of selected) {
          const el = cardRefs.current.get(p.creative_id);
          if (el) {
            // eslint-disable-next-line no-await-in-loop
            const canvas = await captureCard(el, html2canvas);
            captures.push({
              name: p.name,
              size: `${p.width || 300}x${p.height || 250}`,
              dataUrl: canvas.toDataURL('image/png'),
              aspectRatio: canvas.width / canvas.height,
            });
          }
        }

        // Two per slide layout
        const slideW = 13.33;
        const slideH = 7.5;
        const margin = 0.4;
        const labelH = 0.6;
        const maxImgH = slideH - margin * 2 - labelH;

        for (let i = 0; i < captures.length; i += 2) {
          const slide = pptx.addSlide();
          const pair = captures.slice(i, i + 2);
          const cols = pair.length;
          const colW = (slideW - margin * (cols + 1)) / cols;

          pair.forEach((cap: any, col: number) => {
            const x = margin + col * (colW + margin);
            let imgW = colW;
            let imgH = imgW / cap.aspectRatio;
            if (imgH > maxImgH) { imgH = maxImgH; imgW = imgH * cap.aspectRatio; }
            const imgX = x + (colW - imgW) / 2;
            const imgY = margin + labelH;

            slide.addText(cap.name, { x, y: margin, w: colW, h: 0.35, fontSize: 11, bold: true, color: '333333', valign: 'top' });
            slide.addText(cap.size, { x, y: margin + 0.3, w: colW, h: 0.25, fontSize: 9, color: '888888', valign: 'top' });
            slide.addImage({ data: cap.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });
          });
        }

        const saveAs = (await import('file-saver')).saveAs || (await import('file-saver') as any).default?.saveAs || (await import('file-saver') as any).default;
        const blob = await pptx.write({ outputType: 'blob' }) as Blob;
        saveAs(blob, 'gallery-previews.pptx');
      } else {
        const exportCreatives = selected.map((p) => ({
          name: p.name,
          size: `${p.width || 300}x${p.height || 250}`,
          adm: p.adm,
          screenshot_url: p.screenshot_url,
          deviceType: getEffectiveDeviceType(p),
          siteContext,
        }));
        await downloadPPTX(exportCreatives);
      }
    } finally {
      setIsExporting(false);
    }
  }, [gallery, selectedIds, getEffectiveDeviceType, siteContext, viewMode]);

  const handleViewModeChange = useCallback((_: React.MouseEvent, newMode: GalleryViewMode | null) => {
    if (!newMode) return;
    setViewMode(newMode);
    setOptionsPanelOpen(newMode === 'presentation');
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography sx={{ color: 'text.secondary' }}>Loading gallery...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>{error}</Alert>
      </Box>
    );
  }

  if (!gallery) return null;

  const sortedPreviews = sortPreviews(gallery.previews, sortBy);
  const isDark = chromeTheme === 'dark' && viewMode === 'presentation';

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: isDark ? '#121212' : 'background.default', transition: 'background-color 0.3s' }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: isDark ? '#fff' : 'text.primary', transition: 'color 0.3s' }}>
              {gallery.name || 'Preview Gallery'}
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? 'grey.500' : 'text.secondary', transition: 'color 0.3s' }}>
              {gallery.creative_count} creative{gallery.creative_count !== 1 ? 's' : ''}
              {gallery.view_count > 0 && ` \u00B7 ${gallery.view_count} view${gallery.view_count !== 1 ? 's' : ''}`}
            </Typography>
          </Box>

          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={isDark ? { '& .MuiToggleButton-root': { color: 'grey.400', borderColor: '#444', '&.Mui-selected': { bgcolor: 'grey.800', color: '#fff' } } } : {}}
              >
                <ToggleButton value="standard">Standard</ToggleButton>
                <ToggleButton value="presentation">Presentation</ToggleButton>
              </ToggleButtonGroup>

              {viewMode === 'presentation' && (
                <Tooltip title="Options">
                  <IconButton size="small" onClick={() => setOptionsPanelOpen(!optionsPanelOpen)} sx={{ color: isDark ? 'grey.400' : undefined }}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Standard Mode Grid */}
        {viewMode === 'standard' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', alignItems: 'flex-start' }}>
            {sortedPreviews.map((preview) => (
              <Card key={preview.creative_id} variant="outlined" sx={{ display: 'inline-flex', flexDirection: 'column', maxWidth: 380, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 } }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Tooltip title={preview.name} placement="top">
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, alignSelf: 'flex-start', fontSize: '0.75rem', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {preview.name}
                    </Typography>
                  </Tooltip>
                  <GalleryPreviewCard preview={preview} apiHost={apiHost} />
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Presentation Mode Grid */}
        {viewMode === 'presentation' && (
          <Grid container spacing={3}>
            {sortedPreviews.map((preview) => (
              <Grid item xs={12} sm={6} md={4} key={preview.creative_id}>
                <Card variant="outlined" sx={() => {
                  const isSelected = selectedIds.has(preview.creative_id);
                  const defaultBorder = isDark ? '#333' : 'divider';
                  return { height: '100%', overflow: 'hidden', transition: 'all 0.3s', bgcolor: isDark ? '#1e1e1e' : 'background.paper', borderColor: isSelected ? 'primary.main' : defaultBorder, borderWidth: isSelected ? 2 : 1, '&:hover': { boxShadow: 3 } };
                }}>
                  <CardContent>
                    <Tooltip title={preview.name} placement="top">
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: isDark ? '#eee' : 'text.primary', transition: 'color 0.3s', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview.name}
                      </Typography>
                    </Tooltip>
                    <GalleryPresentationCard
                      preview={preview}
                      apiHost={apiHost}
                      deviceType={getEffectiveDeviceType(preview)}
                      siteContext={siteContext}
                      orientation={orientation}
                      chromeTheme={chromeTheme}
                      showSizeLabel={showSizeLabel}
                      showNameLabel={showNameLabel}
                      zoomScale={zoomScale}
                      selected={selectedIds.has(preview.creative_id)}
                      onToggleSelect={handleToggleSelect}
                      onDeviceTypeChange={handleCreativeDeviceTypeChange}
                      cardRef={(el) => {
                        if (el) {
                          cardRefs.current.set(preview.creative_id, el);
                        } else {
                          cardRefs.current.delete(preview.creative_id);
                        }
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: isDark ? 'grey.600' : 'text.secondary', transition: 'color 0.3s' }}>
            Generated by Preview Studio &middot; InMarket OneDSP
          </Typography>
        </Box>
      </Box>

      {/* Options Panel (Presentation mode only) */}
      {viewMode === 'presentation' && (
        <GalleryOptionsPanel
          open={optionsPanelOpen}
          onClose={() => setOptionsPanelOpen(false)}
          deviceType={(() => {
            const types = gallery.previews.map((p) => getEffectiveDeviceType(p));
            const allSame = types.every((t) => t === types[0]);
            return allSame ? types[0] : null;
          })()}
          siteContext={siteContext}
          orientation={orientation}
          chromeTheme={chromeTheme}
          showSizeLabel={showSizeLabel}
          showNameLabel={showNameLabel}
          zoomScale={zoomScale}
          sortBy={sortBy}
          selectedCount={selectedIds.size}
          totalCount={gallery.previews.length}
          onDeviceTypeChange={handleGlobalDeviceTypeChange}
          onSiteContextChange={setSiteContext}
          onOrientationChange={setOrientation}
          onChromeThemeChange={setChromeTheme}
          onShowSizeLabelChange={setShowSizeLabel}
          onShowNameLabelChange={setShowNameLabel}
          onZoomScaleChange={setZoomScale}
          onSortByChange={setSortBy}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          onDownloadPNG={handleDownloadPNG}
          onDownloadPPTX={handleDownloadPPTX}
          isExporting={isExporting}
        />
      )}
    </Box>
  );
}
